// ante-react-kit/src/components/AntQuickPopup/utils/fieldRenderer.js
import React from 'react';
import { TextBox } from 'devextreme-react/text-box';
import { TextArea } from 'devextreme-react/text-area';
import { NumberBox } from 'devextreme-react/number-box';
import { DateBox } from 'devextreme-react/date-box';
import { CheckBox } from 'devextreme-react/check-box';
import { SelectBox } from 'devextreme-react/select-box';
import { TagBox } from 'devextreme-react/tag-box';
import { DropDownBox } from 'devextreme-react/drop-down-box';
import DataGrid, { Column, Selection, Paging } from 'devextreme-react/data-grid';
import AntLocalization from '../../../utils/AntLocalization';
import AntAPI from '../../../utils/AntAPI';

// TODO: editor types not yet supported in fieldRenderer:
//   antColorBox, antSlider, antMonthBox, antYearBox,
//   antDropDownTree, antFilterBuilder, antFormLink, dxSelectBox

/**
 * Render 1 field với editor đã resolve.
 *
 * @param {Object} args
 * @param {Object} args.editor   - { type, lookup, ... } từ resolveEditor
 * @param {*}      args.value
 * @param {Function} args.onValueChanged - (newValue) => void
 * @param {boolean} args.disabled
 * @param {Array}   args.lookupValues  - từ resolveLookup
 * @param {Object}  args.fieldCnf      - field config (placeholder, format...)
 * @param {string}  args.placeholder
 */
export default function fieldRenderer({
    editor, value, onValueChanged, disabled, lookupValues, fieldCnf, placeholder
}) {
    if (!editor) editor = { type: 'antTextBox' };
    const type = editor.type || 'antTextBox';

    const commonProps = {
        value: value == null ? '' : value,
        disabled: !!disabled,
        onValueChanged: (e) => onValueChanged(e.value),
        placeholder: placeholder || ''
    };

    switch (type) {
        case 'antTextBox':
        case 'textBox':
        case 'dxTextBox':
            return <TextBox {...commonProps} />;

        case 'textArea':
            return <TextArea {...commonProps} height={80} />;

        case 'antNumberBox':
        case 'numberBox':
            return <NumberBox {...commonProps} format={fieldCnf?.format} showSpinButtons />;

        case 'antDateBox':
        case 'dateBox':
            return <DateBox {...commonProps} type="date" displayFormat="dd/MM/yyyy" useMaskBehavior />;

        case 'antDateTimeBox':
            return <DateBox {...commonProps} type="datetime" displayFormat="dd/MM/yyyy HH:mm" useMaskBehavior />;

        case 'antCheckBox':
        case 'checkBox':
            return <CheckBox value={!!value} disabled={!!disabled} onValueChanged={(e) => onValueChanged(e.value)} />;

        case 'antSelectBox':
        case 'selectBox':
        case 'dxSelectBox': {
            const items = lookupValues || [];
            return (
                <SelectBox
                    {...commonProps}
                    dataSource={items}
                    valueExpr="code"
                    displayExpr={(item) => (item ? AntLocalization.translateText(item.name) || item.code : '')}
                    showClearButton
                />
            );
        }

        case 'antTagBox':
        case 'tagBox': {
            const items = lookupValues || [];
            return (
                <TagBox
                    {...commonProps}
                    value={Array.isArray(value) ? value : (value ? [value] : [])}
                    dataSource={items}
                    valueExpr="code"
                    displayExpr={(item) => (item ? AntLocalization.translateText(item.name) || item.code : '')}
                    showClearButton
                    onValueChanged={(e) => onValueChanged(e.value)}
                />
            );
        }

        case 'antDropDownGrid':
        case 'dropDownGrid':
            return renderDropDownGrid({ editor, value, onValueChanged, disabled });

        default:
            return <TextBox {...commonProps} />;
    }
}

/**
 * antDropDownGrid: editor.lookup chứa { isRemote, module, table, url, columns,
 *   valueField, displayField, searchConditions, ordering, width, height, searchBar }
 * Dùng new AntAPI(module, table).getList() vì AntAPI không có static post.
 */
function renderDropDownGrid({ editor, value, onValueChanged, disabled }) {
    const lk = editor.lookup || {};
    const valueField = lk.valueField || 'code';
    const displayField = lk.displayField || 'name';
    const width = lk.width || 600;
    const height = lk.height || 360;

    const dataSource = lk.isRemote && lk.module && lk.table
        ? {
              load: async (loadOptions) => {
                  try {
                      const api = new AntAPI(lk.module, lk.table);
                      const params = {
                          ...(lk.searchConditions || {}),
                          ...(loadOptions.searchValue ? { searchText: loadOptions.searchValue } : {}),
                          take: loadOptions.take || 50,
                          skip: loadOptions.skip || 0
                      };
                      const res = await api.getList(params, lk.url || null);
                      const data = (res && res.data) || [];
                      const totalCount = (res && res.totalCount) || data.length;
                      return { data, totalCount };
                  } catch {
                      return { data: [], totalCount: 0 };
                  }
              }
          }
        : (lk.values || []);

    return (
        <DropDownBox
            value={value || ''}
            disabled={!!disabled}
            valueExpr={valueField}
            displayExpr={displayField}
            dataSource={Array.isArray(dataSource) ? dataSource : undefined}
            onValueChanged={(e) => onValueChanged(e.value)}
            dropDownOptions={{ width, height }}
            contentRender={() => (
                <DataGrid
                    dataSource={dataSource}
                    height={height - 20}
                    selectedRowKeys={value ? [value] : []}
                    onSelectionChanged={(e) => {
                        const v = e.selectedRowKeys && e.selectedRowKeys[0];
                        if (v != null) onValueChanged(v);
                    }}
                    keyExpr={valueField}
                    showBorders
                >
                    <Selection mode="single" />
                    <Paging enabled defaultPageSize={50} />
                    {(lk.columns || ['code', 'name']).map((c) => {
                        const col = typeof c === 'string' ? { dataField: c } : c;
                        return <Column key={col.dataField} {...col} />;
                    })}
                </DataGrid>
            )}
        />
    );
}
